/**
 * MusicPlayer — site-wide floating expandable music player.
 * Mounted ONCE at the App root so navigation between pages does not remount
 * the iframe (music keeps playing seamlessly).
 *
 * UX:
 *   - Floating bubble at bottom-right with a music note icon.
 *   - Click → expands into a full-size player card (520px iframe height).
 *   - After the first click, the iframe is kept mounted forever. Closing the
 *     panel only slides it off-screen visually (translate + opacity), so
 *     playback keeps going. This behaves exactly like Spotify's mini-bar.
 *   - Silent (no bubble, no iframe) if the admin has not configured a URL.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const MusicPlayer = () => {
  const [url, setUrl] = useState('');
  const [open, setOpen] = useState(false);
  // Once the panel has been opened at least once we keep the iframe mounted
  // forever so audio survives closes and route changes.
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/home-music`)
      .then((r) => setUrl(r.data?.url || ''))
      .catch(() => {});
  }, []);

  const toggle = () => {
    if (!hasStarted) setHasStarted(true);
    setOpen((v) => !v);
  };

  if (!url) return null;

  return (
    <>
      {/* Floating bubble — always visible */}
      <motion.button
        type="button"
        onClick={toggle}
        data-testid="music-player-toggle"
        aria-label={open ? 'Minimize music player' : 'Open music player'}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 1 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-5 right-5 md:bottom-7 md:right-7 z-40 rounded-full bg-[#D4AF37] text-[#0E4D2A] flex items-center justify-center shadow-[0_10px_25px_rgba(0,0,0,0.4)] hover:shadow-[0_14px_35px_rgba(0,0,0,0.5)] transition-shadow"
        style={{ width: '56px', height: '56px' }}
      >
        <span className="relative flex items-center justify-center">
          {!open && (
            <span
              className={`absolute inset-0 rounded-full bg-[#D4AF37] ${hasStarted ? 'animate-ping opacity-60' : 'animate-ping opacity-40'}`}
            />
          )}
          <span className="relative text-2xl leading-none select-none">
            {open ? '–' : '♪'}
          </span>
        </span>
      </motion.button>

      {/* Player panel — mounted ONCE after first open, then just slides
          in/out. The iframe stays in the DOM so audio survives close &
          route changes. */}
      {hasStarted && (
        <div
          data-testid="music-player-panel"
          className={`fixed bottom-24 right-4 md:right-7 z-40 w-[calc(100vw-2rem)] sm:w-[380px] md:w-[420px] max-w-[440px] max-h-[calc(100vh-7rem)] flex flex-col transition-all duration-300 ease-out ${
            open
              ? 'opacity-100 translate-y-0 pointer-events-auto'
              : 'opacity-0 translate-y-6 pointer-events-none'
          }`}
          aria-hidden={!open}
        >
          <div className="bg-[#0E4D2A] border border-[#D4AF37]/30 rounded-xl shadow-[0_25px_60px_rgba(0,0,0,0.55)] overflow-hidden flex flex-col flex-1 min-h-0">
            <div className="flex items-center justify-between px-4 py-2 border-b border-[#D4AF37]/15 flex-shrink-0">
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] font-sans">
                🎵 Forat Zero · Música
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[#A8B4A6] hover:text-[#F2E8D5] text-lg leading-none w-6 h-6 flex items-center justify-center"
                aria-label="Minimize"
                data-testid="music-player-close"
                title="Minimitza (segueix sonant)"
              >
                –
              </button>
            </div>
            <div className="p-2 flex-1 min-h-0">
              {/* The iframe fills whatever space is available inside the
                  flex column, so the header is always visible even on
                  short viewports. Its DOM position is stable so the audio
                  session persists across close/open and route changes. */}
              <iframe
                key="site-music-iframe"
                src={url}
                title="Forat Zero music player"
                loading="lazy"
                allow="autoplay; encrypted-media"
                className="w-full h-full block border-0 rounded-lg"
                style={{ minHeight: '260px' }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MusicPlayer;
